import React, { useState, useRef, useEffect } from 'react';
import { formatCurrency } from '../../utils/format';

interface Props {
  value: number;
  onSave: (newValue: number) => Promise<void>;
  disabled?: boolean;
  label?: string;
}

export function EditablePriceCell({ value, onSave, disabled, label }: Props) {
  const [editing, setEditing] = useState(false);
  const [tempValue, setTempValue] = useState(String(value));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) setTempValue(String(value));
  }, [value, editing]);

  const commit = async () => {
    const num = parseFloat(tempValue);
    if (isNaN(num) || num < 0 || num === value) {
      setTempValue(String(value));
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(num);
      setEditing(false);
    } catch {
      setTempValue(String(value));
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { e.preventDefault(); commit(); }
    if (e.key === 'Escape') { setTempValue(String(value)); setEditing(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="number"
        min="0"
        step="0.01"
        value={tempValue}
        onChange={e => setTempValue(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        disabled={saving || disabled}
        aria-label={label}
        style={{
          width: '80px',
          background: 'var(--glass-bg-card)',
          border: '1px solid var(--color-primary)',
          borderRadius: 'var(--radius-interactive)',
          color: 'var(--color-text)',
          fontSize: 'var(--text-xs)',
          padding: '0.125rem 0.375rem',
          outline: 'none',
          opacity: saving ? 0.6 : 1,
          boxSizing: 'border-box',
        }}
      />
    );
  }

  return (
    <span
      onClick={e => { e.stopPropagation(); if (!disabled) setEditing(true); }}
      title="Clicca per modificare"
      style={{
        cursor: disabled ? 'default' : 'pointer',
        borderBottom: '1px dashed var(--color-text-faint)',
        paddingBottom: '1px',
      }}
    >
      {formatCurrency(value)}
    </span>
  );
}
